import { useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import type {
  ApiClient, KuehllastLaufKopf, KuehllastLaufDetail, KuehllastErgebnisRaum,
  KuehllastRaumDetail, KuehllastJahresgang, KuehllastStunde,
  KuehllastAnlagenVergleich,
  KuehllastReferenzvergleich,
  KuehllastJahresgangErgebnis, AuslegungslaufWahl,
} from '../api';
import {
  IconEbene, IconRaum, IconSuche, IconWarnung, IconKuehlung, IconLueftung,
  IconPdf,
} from '../Icons';
import { Berichtsknoepfe } from './Lastbericht';
import { GewerkUnterlagenZip } from '../UnterlagenZipKnopf';
import { Auslegungshinweis, Kennzeichnen } from './heizlastkuehllast/Laufkennzeichnung';

// =============================================================================
//  Gemeinsames
// =============================================================================

const zahl = (n: number | null | undefined, z = 0) =>
  n === null || n === undefined ? '—' : n.toLocaleString('de-DE', {
    minimumFractionDigits: z, maximumFractionDigits: z });

/**
 * Die Anteile in fester Reihenfolge und mit fester Farbe.
 *
 * Fest, damit ein Betrachter sie zwischen Raum, Gebaeude und Jahresgang
 * wiedererkennt. Die Reihenfolge folgt der Groesse, die sie in einer
 * gewoehnlichen Kuehllast haben - Strahlung zuerst, Lueftung zuletzt.
 */
const ANTEILE = [
  { key: 'strahlungW', bez: 'Solarer Eintrag', farbe: '#e8a33d' },
  { key: 'innenW', bez: 'Innere Lasten', farbe: '#c25e5e' },
  { key: 'opakW', bez: 'Opake Bauteile', farbe: '#7aa7c7' },
  { key: 'transparentW', bez: 'Transmission Glas', farbe: '#9fc3d9' },
  { key: 'lueftungW', bez: 'Lüftung', farbe: '#6a9a6a' },
] as const;

const MONATE = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun',
                'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];

// =============================================================================
//  Der Tagesgang als gestapelte Flächen
// =============================================================================

/**
 * 24 Stunden, gestapelt nach Anteilen, mit der Aussentemperatur als Linie.
 *
 * Eigenes SVG statt einer Diagrammbibliothek - dieselbe Entscheidung wie bei
 * den Sinnbildern: Es ist eine Kurve mit 24 Punkten, und eine Abhaengigkeit
 * dafuer einzuhandeln lohnt nicht.
 *
 * Negative Werte werden NICHT abgeschnitten. Nachts kann die Summe unter null
 * liegen - dann kuehlt das Gebaeude sich selbst aus, und genau dieser
 * Nulldurchgang ist die Stunde, an der eine Change-over-Anlage umschaltet.
 */
function Tagesgang({ stunden, hoehe = 190 }: {
  stunden: KuehllastStunde[];
  hoehe?: number;
}) {
  if (stunden.length === 0)
    return <p className="sehr-schwach">Kein Stundengang vorhanden.</p>;

  const breite = 720;
  const links = 52, rechts = 46, oben = 12, unten = 26;
  const zeichenB = breite - links - rechts;
  const zeichenH = hoehe - oben - unten;

  // Die Achse muss beide Vorzeichen fassen.
  let max = 0, min = 0;
  for (const s of stunden) {
    let auf = 0, ab = 0;
    for (const a of ANTEILE) {
      const v = s[a.key];
      if (v > 0) auf += v; else ab += v;
    }
    max = Math.max(max, auf);
    min = Math.min(min, ab);
  }
  if (max === min) max = min + 1;

  const y = (w: number) =>
    oben + zeichenH * (1 - (w - min) / (max - min));
  const x = (h: number) => links + zeichenB * (h / 23);

  const nulllinie = y(0);

  // Temperaturachse rechts
  const temps = stunden.map((s) => s.aussenC).filter((t): t is number => t !== null);
  const tMax = temps.length > 0 ? Math.max(...temps) : 1;
  const tMin = temps.length > 0 ? Math.min(...temps) : 0;
  const yT = (t: number) => oben + zeichenH
    * (1 - (t - tMin) / (tMax - tMin === 0 ? 1 : tMax - tMin));

  // Gestapelte Bänder: getrennt nach oben und unten aufgetragen.
  const baender: { farbe: string; punkte: string }[] = [];

  for (const a of ANTEILE) {
    const oberkante: string[] = [];
    const unterkante: string[] = [];

    stunden.forEach((s, i) => {
      let basis = 0;
      for (const b of ANTEILE) {
        if (b.key === a.key) break;
        const v = s[b.key];
        // Nur gleichsinnige Anteile stapeln, sonst hebt sich das Band auf.
        if (Math.sign(v) === Math.sign(s[a.key])) basis += v;
      }
      const wert = s[a.key];
      oberkante.push(`${x(i)},${y(basis + wert)}`);
      unterkante.unshift(`${x(i)},${y(basis)}`);
    });

    baender.push({
      farbe: a.farbe,
      punkte: [...oberkante, ...unterkante].join(' '),
    });
  }

  const linie = stunden
    .map((s, i) => `${i === 0 ? 'M' : 'L'}${x(i)},${y(s.trockenW)}`)
    .join(' ');

  const tempLinie = stunden
    .map((s, i) => s.aussenC === null
      ? null : `${i === 0 ? 'M' : 'L'}${x(i)},${yT(s.aussenC)}`)
    .filter(Boolean).join(' ');

  const beschriftung = (w: number) =>
    Math.abs(w) >= 1000 ? `${zahl(w / 1000, 0)} kW` : `${zahl(w, 0)} W`;

  return (
    <div style={{ overflowX: 'auto' }}>
      <svg viewBox={`0 0 ${breite} ${hoehe}`} width="100%"
           style={{ maxWidth: breite, display: 'block' }}
           role="img" aria-label="Tagesgang der Kühllast">
        {/* Waagerechte Hilfslinien */}
        {[0, 0.25, 0.5, 0.75, 1].map((f) => {
          const w = min + (max - min) * f;
          return (
            <g key={f}>
              <line x1={links} x2={breite - rechts} y1={y(w)} y2={y(w)}
                    stroke="var(--linie)" strokeWidth={1} />
              <text x={links - 6} y={y(w) + 3} textAnchor="end"
                    fontSize={10} fill="var(--text-sehr-schwach)">
                {beschriftung(w)}
              </text>
            </g>
          );
        })}

        {baender.map((b, i) => (
          <polygon key={i} points={b.punkte} fill={b.farbe} fillOpacity={0.75} />
        ))}

        {/* Die Nulllinie liegt ueber den Baendern - sie ist die Aussage. */}
        <line x1={links} x2={breite - rechts} y1={nulllinie} y2={nulllinie}
              stroke="var(--anthrazit)" strokeWidth={1} />

        <path d={linie} fill="none" stroke="var(--anthrazit)" strokeWidth={1.6} />

        {tempLinie && (
          <path d={tempLinie} fill="none" stroke="var(--schlecht)"
                strokeWidth={1.2} strokeDasharray="4 3" />
        )}

        {/* Stundenachse */}
        {[0, 3, 6, 9, 12, 15, 18, 21].map((h) => (
          <text key={h} x={x(h)} y={hoehe - 8} textAnchor="middle"
                fontSize={10} fill="var(--text-sehr-schwach)">
            {h}:00
          </text>
        ))}

        {temps.length > 0 && (
          <>
            <text x={breite - rechts + 6} y={yT(tMax) + 3} fontSize={10}
                  fill="var(--schlecht)">{zahl(tMax, 1)} °C</text>
            <text x={breite - rechts + 6} y={yT(tMin) + 3} fontSize={10}
                  fill="var(--schlecht)">{zahl(tMin, 1)} °C</text>
          </>
        )}
      </svg>

      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap',
                    marginTop: 6, fontSize: 12 }}>
        {ANTEILE.map((a) => (
          <span key={a.key} style={{ display: 'inline-flex',
                                     alignItems: 'center', gap: 5 }}>
            <span style={{ width: 10, height: 10, borderRadius: 2,
                           background: a.farbe }} />
            {a.bez}
          </span>
        ))}
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}
              className="sehr-schwach">
          <span style={{ width: 14, height: 0,
                         borderTop: '1px dashed var(--schlecht)' }} />
          Außentemperatur
        </span>
      </div>
    </div>
  );
}

// =============================================================================
//  Die Routen-Wrapper (Muster N1) — Kühllast und Kühllast-Jahresgang
// =============================================================================
//
// `KuehllastReiter` und `JahresgangReiter` fetchen sich schon immer selbst
// alles, was sie brauchen (`api`, `projektId`) - anders als "Heizlast",
// "Klima" und "Allgemein" in `Gebaeudehuelle.tsx`, die bis N1 einen
// gemeinsamen Effekt teilten. Deshalb genügt hier ein duenner Wrapper, der
// den Outlet-Kontext von `HeizlastKuehllastRahmen` auspackt - keine
// Extraktion von JSX, kein neuer Zustand.

/** Route `heizlast-kuehllast/kuehllast`. */
export function KuehllastSeite() {
  const { api, projekt } = useOutletContext<{ api: ApiClient; projekt: { id: string } }>();
  return <KuehllastReiter api={api} projektId={projekt.id} />;
}

/** Route `heizlast-kuehllast/jahresgang` — der Jahresgang der KÜHLLAST.
 *  Der Jahresgang der Heizlast (`Heizjahresgang.tsx`) bleibt eine
 *  Unterkomponente der Route "Heizlast": Er ist an den jeweils jüngsten
 *  Heizlastlauf gebunden (`lauf={laeufe[0]}`) und hat ohne ihn keinen
 *  eigenen Inhalt - eine eigene Route würde nur den Lauf ein zweites Mal
 *  laden. */
export function KuehllastJahresgangSeite() {
  const { api, projekt } = useOutletContext<{ api: ApiClient; projekt: { id: string } }>();
  return <JahresgangReiter api={api} projektId={projekt.id} />;
}

// =============================================================================
//  Der Kühllast-Reiter
// =============================================================================

export function KuehllastReiter({ api, projektId }: {
  api: ApiClient; projektId: string;
}) {
  const [laeufe, setLaeufe] = useState<KuehllastLaufKopf[]>([]);
  const [detail, setDetail] = useState<KuehllastLaufDetail | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  // TL1: der maßgebende Kühllastlauf (Auslegungslauf.cs), nicht l[0].
  const [wahl, setWahl] = useState<AuslegungslaufWahl | null>(null);

  // Standard AN.
  //
  // BEFUND 16.09.2026 (Fachplaner: „bei der kühllast fehlt der jahresgang"):
  // Ein Lauf ohne Jahresgang entsteht leicht und fällt erst Wochen später
  // auf. Wer die Minuten sparen will, muss das Häkchen ausdrücklich
  // wegnehmen — und sieht dabei, was er weglässt.
  const [mitJahresgang, setMitJahresgang] = useState(true);

  useEffect(() => {
    let weg = false;
    (async () => {
      try {
        const [l, a] = await Promise.all([
          api.kuehllastLaeufe(projektId), api.kuehllastAuslegungslauf(projektId)]);
        if (weg) return;
        setLaeufe(l); setWahl(a.wahl);
        if (a.wahl) {
          const d = await api.kuehllastLauf(a.wahl.laufId);
          if (!weg) setDetail(d);
        }
      } catch (err) {
        if (!weg) setFehler(err instanceof Error ? err.message : String(err));
      }
    })();
    return () => { weg = true; };
  }, [api, projektId]);

  const rechnen = async () => {
    setLaeuft(true);
    setFehler(null);
    try {
      const e = await api.kuehllastRechnen(projektId, undefined, mitJahresgang);
      const [l, a] = await Promise.all([
        api.kuehllastLaeufe(projektId), api.kuehllastAuslegungslauf(projektId)]);
      setLaeufe(l); setWahl(a.wahl);
      setDetail(await api.kuehllastLauf(e.laufId));
    } catch (err) {
      setFehler(err instanceof Error ? err.message : String(err));
    } finally {
      setLaeuft(false);
    }
  };

  const lauf = detail?.lauf;

  // TL1: Kennzeichnung "maßgebend" - danach Liste und Wahl neu laden.
  const kennzeichnen = async (laufId: string, begruendung: string, zurueck: boolean) => {
    const antwort = await api.kuehllastMassgebend(laufId, begruendung, zurueck);
    const [l, a] = await Promise.all([
      api.kuehllastLaeufe(projektId), api.kuehllastAuslegungslauf(projektId)]);
    setLaeufe(l); setWahl(a.wahl);
    return antwort.message;
  };
  const gezeigt = lauf ? (laeufe.find((l) => l.id === lauf.id) ?? lauf) : null;

  return (
    <>
      <div className="karte">
        <div className="bereich-kopf">
          <h2 className="karte-titel" style={{ margin: 0,
                display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <IconKuehlung size={18} /> Kühllast rechnen
          </h2>
          {laeufe.length > 0 && (
            <span className="marke neutral">{laeufe.length} Läufe</span>
          )}
        </div>

        <details className="erlaeuterung">
          <summary>Erläuterung</summary>
          <div>
            Auslegungstag stundenweise nach VDI 2078: opake Bauteile über die
            Sonnenlufttemperatur, Verglasung über g-Wert und Einstrahlung,
            innere Lasten über die Stundenprofile der Nutzung, Lüftung mit
            Rückgewinnung. Anschließend derselbe Rechenweg über alle 8.760
            Stunden des Jahres für den Jahresgang. Trockene und feuchte Last
            bleiben getrennt — die feuchte geht an die Entfeuchtung, nicht an
            den Raumkühler.
          </div>
        </details>

        <div style={{ display: 'flex', gap: 14, alignItems: 'center',
                      flexWrap: 'wrap' }}>
          <button className="knopf betont" disabled={laeuft}
                  onClick={() => void rechnen()}>
            {laeuft ? 'rechnet …' : 'Kühllast rechnen'}
          </button>

          <label style={{ display: 'inline-flex', alignItems: 'center',
                          gap: 6 }}>
            <input type="checkbox" checked={mitJahresgang} disabled={laeuft}
                   onChange={(e) => setMitJahresgang(e.target.checked)} />
            mit Jahresgang
          </label>

          {!mitJahresgang && (
            <span className="sehr-schwach">
              Ohne Jahresgang bleibt der Reiter „Jahresgang" für diesen Lauf
              leer. Nachtragen geht dort jederzeit.
            </span>
          )}
        </div>

        {fehler && (
          <div className="hinweis fehler" style={{ marginTop: 10 }}>
            <span className="hinweis-zeichen">!</span><div>{fehler}</div>
          </div>
        )}

        {laeufe.length > 0 && (
          <Auslegungshinweis art="Kühllast" wahl={wahl} gezeigt={gezeigt} />
        )}

        {gezeigt && (
          <Kennzeichnen laufnummer={gezeigt.laufnummer}
                        istMassgebend={gezeigt.istMassgebend ?? false}
                        beiSenden={(b, z) => kennzeichnen(gezeigt.id, b, z)} />
        )}

        {lauf && (
          <>
            <div className="kennzahlen" style={{ marginTop: 18 }}>
              <div>
                <div className="kennzahl-wert">
                  {zahl((lauf.spitzeTrockenW ?? 0) / 1000, 0)}
                </div>
                <div className="kennzahl-name">kW Gebäudespitze</div>
              </div>
              <div>
                <div className="kennzahl-wert">{lauf.spitzeStunde ?? '—'}:00</div>
                <div className="kennzahl-name">Stunde der Spitze</div>
              </div>
              <div>
                <div className="kennzahl-wert">
                  {zahl((lauf.summeRaumspitzenW ?? 0) / 1000, 0)}
                </div>
                <div className="kennzahl-name">kW Summe der Raumspitzen</div>
              </div>
              <div>
                <div className="kennzahl-wert">
                  {lauf.gleichzeitigkeit === null
                    ? '—' : `${zahl(lauf.gleichzeitigkeit * 100, 0)} %`}
                </div>
                <div className="kennzahl-name">Gleichzeitigkeit</div>
              </div>
              <div>
                <div className="kennzahl-wert">
                  {zahl((lauf.spitzeFeuchtW ?? 0) / 1000, 0)}
                </div>
                <div className="kennzahl-name">kW latent</div>
              </div>
              <div>
                <div className="kennzahl-wert">
                  {zahl(lauf.aussentemperaturMaxC, 1)}
                </div>
                <div className="kennzahl-name">°C Tagesmaximum</div>
              </div>
            </div>

            <p className="sehr-schwach" style={{ marginTop: 10 }}>
              Auslegungstag {lauf.auslegungTag}.{lauf.auslegungMonat}.,{' '}
              {lauf.anzahlRaeume} Räume, {lauf.anzahlStunden} Stunden.
              Die Gebäudespitze ist kleiner als die Summe der Raumspitzen:
              Ostzimmer haben ihre Spitze vormittags, Westzimmer nachmittags.
            </p>

            {lauf.vorbehalte && (
              <div className="hinweis warnung" style={{ marginTop: 12 }}>
                <span className="hinweis-zeichen">!</span>
                <div>
                  <strong>Vorbehalte dieses Laufs</strong>
                  <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                    {lauf.vorbehalte.split('; ').map((v, i) => (
                      <li key={i}>{v}</li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {detail && (
        <div className="karte" style={{ marginTop: 18 }}>
          <AnlagenVergleich vergleich={detail.anlagenVergleich} />
        </div>
      )}

      {detail?.referenzVergleich && (
        <div className="karte" style={{ marginTop: 18 }}>
          <ReferenzVergleich vergleich={detail.referenzVergleich} />
        </div>
      )}

      {detail && (
        <div className="karte" style={{ marginTop: 18 }}>
          <Berichtsknoepfe
            ebenen={[...new Set(detail.raeume
              .map((r) => r.ebene).filter((e): e is string => !!e))].sort()}
            laden={(umfang, ebene) =>
              api.kuehllastBericht(detail.lauf.id, umfang, ebene)}
            excel={() => api.kuehllastListe(projektId, detail.lauf.id)} />
        </div>
      )}

      {detail && detail.tagesgang.length > 0 && (
        <div className="karte" style={{ marginTop: 18 }}>
          <h3 className="karte-titel">Tagesgang des Gebäudes</h3>
          <Tagesgang stunden={detail.tagesgang} hoehe={220} />
        </div>
      )}

      {detail && detail.raeume.length > 0 && (
        <div style={{ marginTop: 18 }}>
          <Raumansicht api={api} detail={detail} />
        </div>
      )}
    </>
  );
}

// =============================================================================
//  Bedarf gegen installierte Kälteleistung
// =============================================================================

/**
 * Die fachlich wichtigste Aussage der Seite: Deckt die installierte
 * Kälteleistung (Change-Over-Register laut Herstellerdaten) den gerechneten
 * Bedarf im Auslegungsfall? Deshalb Projektsumme UND je Anlage, und eine
 * Überschreitung ist auf den ersten Blick erkennbar (Marke, Farbe, eigene
 * Tabellenzeile) statt einer Zahl, die man im Kopf gegenrechnen muss.
 *
 * `vergleich` ist NULL, wenn das Projekt keine Change-Over-Register hat —
 * dann bleibt der Abschnitt bei der Erläuterung, kein „0 kW".
 */
/**
 * Der Abgleich gegen die Kälteleistungszahl des Fachplaners — Befund H-20 der
 * Normprüfung vom 16.09.2026.
 *
 * ═══ Warum ZWEI eigene Zahlen gegen eine fremde stehen ════════════════════
 *
 * Die Anlagenkälteleistung ist die Summe der Einzelspitzen: jeder Raum zu
 * seiner eigenen Stunde. Für die Auslegung der Register ist das richtig —
 * jedes muss seine Spitze decken. Für die Kälteerzeugung ist es zu groß, denn
 * die Stunden gibt es nicht gleichzeitig. Die Gebäudespitze dagegen ist
 * gleichzeitig, aber sie ist die Raumkühllast und nicht die
 * Außenluftaufbereitung.
 *
 * Keine der beiden wird als „die“ Zahl ausgegeben — welche zu bemessen ist,
 * entscheidet der Fachplaner (Frage 8 der Normprüfung).
 *
 * ═══ Warum der Vorbehalt unter jeder Zahl steht ═══════════════════════════
 *
 * Die Referenzzahl ist die GRÖSSERE Menge: Sie enthält zusätzlich statische
 * Kühlung, IT/ELT und Kleinkälte. Wer die Abweichung ohne diesen Satz liest,
 * hält sie für kleiner, als sie ist.
 */
function ReferenzVergleich({ vergleich }: {
  vergleich: KuehllastReferenzvergleich;
}) {
  const gewertet = vergleich.zeilen.filter((z) => z.istAktuell);

  return (
    <>
      <div className="bereich-kopf">
        <h2 className="karte-titel" style={{ margin: 0,
              display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <IconKuehlung size={18} /> Gegen Referenz
        </h2>
        {gewertet.some((z) => z.ueberToleranz) && (
          <span className="marke schlecht">
            außerhalb der Toleranz
          </span>
        )}
      </div>

      <p className="sehr-schwach" style={{ marginTop: 4 }}>
        Derselbe Abgleich, den die Heizlast schon hat: die eigene Zahl gegen
        die des Fachplaners. Der externe Wert ist eine zweite Meinung und kein
        Sollwert — die Abweichung ist zu erklären, nicht wegzurechnen.
      </p>

      <div className="kennzahlen" style={{ marginTop: 14 }}>
        <div>
          <div className="kennzahl-wert">
            {zahl(vergleich.anlagenKaelteleistungKw, 0)}
          </div>
          <div className="kennzahl-name">
            kW Anlagenkälteleistung (Summe der Einzelspitzen)
          </div>
        </div>
        <div>
          <div className="kennzahl-wert">
            {zahl(vergleich.gebaeudespitzeKw, 0)}
          </div>
          <div className="kennzahl-name">
            kW gleichzeitige Gebäudespitze
            {vergleich.spitzeStunde === null
              ? '' : ` um ${vergleich.spitzeStunde}:00`}
          </div>
        </div>
        <div>
          <div className="kennzahl-wert">
            {gewertet.length === 0
              ? '—' : zahl(gewertet[0].referenzKw, 0)}
          </div>
          <div className="kennzahl-name">
            kW Referenz{gewertet.length === 0
              ? ' (keine aktuelle)' : ` (${gewertet[0].szenario})`}
          </div>
        </div>
      </div>

      <div className="tabellen-rahmen" style={{ marginTop: 14 }}>
        <table>
          <thead>
            <tr>
              <th>Szenario</th>
              <th>Quelle</th>
              <th style={{ textAlign: 'right' }}>Referenz kW</th>
              <th style={{ textAlign: 'right' }}>Anlagenkälte kW</th>
              <th style={{ textAlign: 'right' }}>Abw.</th>
              <th style={{ textAlign: 'right' }}>Gebäudespitze kW</th>
              <th style={{ textAlign: 'right' }}>Abw.</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {vergleich.zeilen.map((z) => (
              <tr key={z.szenario}
                  style={{ opacity: z.istAktuell ? 1 : 0.55 }}>
                <td>{z.szenario}</td>
                <td className="sehr-schwach">{z.quelle ?? '—'}</td>
                <td style={{ textAlign: 'right' }}>
                  {zahl(z.referenzKw, 0)}
                </td>
                <td style={{ textAlign: 'right' }}>
                  {zahl(z.anlagenKaelteleistungKw, 0)}
                </td>
                <td style={{
                  textAlign: 'right',
                  color: z.istAktuell && z.ueberToleranz
                    ? 'var(--schlecht)' : undefined,
                  fontWeight: z.istAktuell && z.ueberToleranz ? 600 : undefined,
                }}>
                  {z.abweichungAnlageProzent > 0 ? '+' : ''}
                  {zahl(z.abweichungAnlageProzent, 1)} %
                </td>
                <td style={{ textAlign: 'right' }}>
                  {zahl(z.gebaeudespitzeKw, 0)}
                </td>
                <td style={{ textAlign: 'right' }}>
                  {z.abweichungSpitzeProzent > 0 ? '+' : ''}
                  {zahl(z.abweichungSpitzeProzent, 1)} %
                </td>
                <td className="sehr-schwach">
                  {z.istAktuell
                    ? `gewertet, Toleranz ${zahl(z.toleranzProzent, 0)} %`
                    : `nicht gewertet: ${z.datenstatus ?? 'ohne Status'}`}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="hinweis warnung" style={{ marginTop: 12 }}>
        <span className="hinweis-zeichen">!</span>
        <div>{vergleich.vorbehalt}</div>
      </div>
    </>
  );
}

function AnlagenVergleich({ vergleich }: {
  vergleich: KuehllastAnlagenVergleich | null;
}) {
  return (
    <>
      <div className="bereich-kopf">
        <h2 className="karte-titel" style={{ margin: 0,
              display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <IconKuehlung size={18} /> Bedarf gegen installierte Kälteleistung
        </h2>
        {vergleich && (
          <span className={`marke ${vergleich.uebersteigtGesamt ? 'schlecht' : 'gut'}`}>
            {vergleich.uebersteigtGesamt
              ? 'Bedarf übersteigt die Installation'
              : 'Installation deckt den Bedarf'}
          </span>
        )}
      </div>

      <p className="sehr-schwach" style={{ marginTop: 4 }}>
        Die Anlagenkälteleistung — die Leistung zur Aufbereitung der
        Außenluft im Auslegungsfall, nicht die Raumkühllast — gegen die
        installierte Leistung der Change-Over-Register laut Herstellerdaten
        (<code>plant_units</code>). Das ist die Antwort auf die Frage hinter
        der Kühllastprüfung: Deckt die installierte Kälteleistung den
        Auslegungsfall?
      </p>

      {!vergleich && (
        <p className="sehr-schwach" style={{ marginTop: 10 }}>
          Kein Vergleich möglich — keine Change-Over-Register
          (plant_units.Art „CHANGE") im Projekt gefunden.
        </p>
      )}

      {vergleich && (
        <>
          <div className="kennzahlen" style={{ marginTop: 14 }}>
            <div>
              <div className="kennzahl-wert">
                {zahl(vergleich.bedarfGesamtW / 1000, 0)}
              </div>
              <div className="kennzahl-name">kW Bedarf (Projektsumme)</div>
            </div>
            <div>
              <div className="kennzahl-wert">
                {zahl(vergleich.installiertGesamtW / 1000, 0)}
              </div>
              <div className="kennzahl-name">
                kW installiert ({vergleich.anzahlEinheiten} Einheiten,{' '}
                {vergleich.anzahlAnlagenInstalliert} Anlagen)
              </div>
            </div>
            <div>
              <div className="kennzahl-wert"
                   style={{ color: vergleich.uebersteigtGesamt
                     ? 'var(--schlecht)' : 'var(--gut)' }}>
                {vergleich.uebersteigtGesamt ? '−' : '+'}
                {zahl(Math.abs(vergleich.differenzGesamtW) / 1000, 0)}
              </div>
              <div className="kennzahl-name">kW Differenz</div>
            </div>
          </div>

          {vergleich.uebersteigtGesamt && (
            <div className="hinweis fehler" style={{ marginTop: 12 }}>
              <span className="hinweis-zeichen">!</span>
              <div>
                Der gerechnete Kältebedarf im Auslegungsfall übersteigt in
                der Projektsumme die installierte Leistung der
                Change-Over-Register um{' '}
                {zahl(Math.abs(vergleich.differenzGesamtW) / 1000, 0)} kW —
                Befund für den Fachplaner, kein Rechenfehler.
              </div>
            </div>
          )}

          {vergleich.jeAnlage.length > 0 ? (
            <table className="liste" style={{ marginTop: 14 }}>
              <thead>
                <tr>
                  <th>Anlage</th>
                  <th style={{ textAlign: 'right' }}>Bedarf kW</th>
                  <th style={{ textAlign: 'right' }}>Installiert kW</th>
                  <th style={{ textAlign: 'right' }}>Differenz kW</th>
                </tr>
              </thead>
              <tbody>
                {vergleich.jeAnlage.map((v) => (
                  <tr key={v.plantId}
                      className={v.uebersteigt
                        ? 'kaelteleistung-ueberschritten' : undefined}>
                    <td>{v.anlage}</td>
                    <td style={{ textAlign: 'right' }}>
                      {zahl(v.bedarfW / 1000, 1)}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {v.hatRegister
                        ? zahl(v.installiertW / 1000, 1)
                        : <span className="sehr-schwach"
                                title="Keine Change-Over-Register für diese Anlage — installierte Leistung nicht 0, sondern unbekannt">
                            —
                          </span>}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {v.hatRegister
                        ? <strong style={{ color: v.uebersteigt
                              ? 'var(--schlecht)' : 'var(--gut)' }}>
                            {v.uebersteigt ? '−' : '+'}
                            {zahl(Math.abs(v.differenzW) / 1000, 1)}
                          </strong>
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="sehr-schwach" style={{ marginTop: 12 }}>
              Kein Vergleich je Anlage: Keine Raumzeile mit belegter
              Kühl-Zulufttemperatur trägt eine belastbare
              Raum→Anlage-Zuordnung — nur die Projektsumme oben gilt.
            </p>
          )}
        </>
      )}
    </>
  );
}

// =============================================================================
//  Baum und Raumblatt
// =============================================================================

function Raumansicht({ api, detail }: {
  api: ApiClient; detail: KuehllastLaufDetail;
}) {
  const [suche, setSuche] = useState('');
  const [offen, setOffen] = useState<Set<string>>(new Set());
  const [gewaehlt, setGewaehlt] = useState<string | null>(null);
  const [raumdetail, setRaumdetail] = useState<KuehllastRaumDetail | null>(null);
  const [laedt, setLaedt] = useState(false);

  const ebenen = useMemo(() => {
    const s = suche.trim().toLowerCase();
    const passt = (r: KuehllastErgebnisRaum) => s === '' || [
      r.raumnummer, r.raumname, r.ebene, r.nutzung,
    ].some((f) => f?.toLowerCase().includes(s));

    const nach = new Map<string, KuehllastErgebnisRaum[]>();
    for (const r of detail.raeume) {
      if (!passt(r)) continue;
      const e = r.ebene ?? 'Ohne Ebene';
      const liste = nach.get(e);
      if (liste) liste.push(r); else nach.set(e, [r]);
    }

    return [...nach.entries()]
      .map(([name, raeume]) => ({
        name,
        raeume: [...raeume].sort(
          (a, b) => (b.spitzeTrockenW ?? 0) - (a.spitzeTrockenW ?? 0)),
        summeW: raeume.reduce((s2, r) => s2 + (r.spitzeTrockenW ?? 0), 0),
      }))
      .sort((a, b) => a.name.localeCompare(b.name, 'de'));
  }, [detail.raeume, suche]);

  const raum = detail.raeume.find((r) => r.id === gewaehlt) ?? null;

  const waehlen = async (id: string) => {
    setGewaehlt(id);
    setRaumdetail(null);
    setLaedt(true);
    try {
      setRaumdetail(await api.kuehllastRaum(detail.lauf.id, id));
    } finally {
      setLaedt(false);
    }
  };

  const gesamt = ebenen.reduce((s, e) => s + e.raeume.length, 0);

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr',
                  gap: 18, alignItems: 'start' }}>
      <div className="karte" style={{ padding: 0, position: 'sticky', top: 16 }}>
        <div style={{ padding: '14px 14px 10px' }}>
          <div style={{ position: 'relative' }}>
            <span style={{ position: 'absolute', left: 9, top: '50%',
                           transform: 'translateY(-50%)',
                           color: 'var(--text-sehr-schwach)',
                           pointerEvents: 'none' }}>
              <IconSuche size={15} />
            </span>
            <input value={suche} onChange={(e) => setSuche(e.target.value)}
                   placeholder="Nummer, Name oder Nutzung …"
                   style={{ width: '100%', paddingLeft: 30 }} />
          </div>
          <p className="sehr-schwach" style={{ margin: '6px 0 0',
                display: 'flex', justifyContent: 'space-between' }}>
            <span>{gesamt} Räume</span>
            <span title="Trockene Raumkühllast / Anlagenkälteleistung">
              kW Raum · kW Anlage
            </span>
          </p>
        </div>

        <div style={{ maxHeight: 'calc(100vh - 220px)', overflowY: 'auto',
                      borderTop: '1px solid var(--linie)' }}>
          {ebenen.map((e) => (
            <div key={e.name}>
              <button
                onClick={() => setOffen((alt) => {
                  const neu = new Set(alt);
                  if (neu.has(e.name)) neu.delete(e.name); else neu.add(e.name);
                  return neu;
                })}
                style={{ width: '100%', textAlign: 'left', border: 'none',
                         borderBottom: '1px solid #f0f0f0', borderRadius: 0,
                         padding: '9px 14px', fontWeight: 600, fontSize: 13,
                         background: 'var(--flaeche)' }}>
                <span className="baum-ebene" style={{
                  display: 'inline-flex', alignItems: 'center',
                  gap: 7, width: '100%' }}>
                  <span style={{ width: 10, fontSize: 11,
                                 color: 'var(--text-sehr-schwach)' }}>
                    {offen.has(e.name) ? '▾' : '▸'}
                  </span>
                  <IconEbene size={15} />
                  {e.name}
                  <span className="sehr-schwach" style={{ fontWeight: 400 }}>
                    ({e.raeume.length})
                  </span>
                  <span className="sehr-schwach" style={{
                    marginLeft: 'auto', fontWeight: 400 }}>
                    {zahl(e.summeW / 1000, 1)} kW
                  </span>
                </span>
              </button>

              {offen.has(e.name) && e.raeume.map((r) => (
                <button key={r.id} onClick={() => void waehlen(r.id)}
                        title={r.raumname ?? undefined}
                        style={{
                          width: '100%', textAlign: 'left', border: 'none',
                          borderBottom: '1px solid #f7f7f7', borderRadius: 0,
                          padding: '7px 14px 7px 30px', fontSize: 12,
                          background: gewaehlt === r.id
                            ? '#e8f1f8' : 'transparent',
                          fontWeight: gewaehlt === r.id ? 600 : 400,
                          whiteSpace: 'nowrap', overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}>
                  <span className="baum-raum" style={{
                    display: 'inline-flex', alignItems: 'center',
                    gap: 6, width: '100%' }}>
                    <IconRaum size={13} />
                    <strong>{r.raumnummer ?? '—'}</strong>
                    <span className="sehr-schwach" style={{
                      overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {r.raumname}
                    </span>
                    {r.personen === null && (
                      <IconWarnung size={12} color="var(--warnung)"
                                   title="Keine Personenzahl" />
                    )}
                    <span className="sehr-schwach" style={{
                      marginLeft: 'auto', fontWeight: 400,
                      textAlign: 'right', minWidth: 28 }}
                          title="Trockene Raumkühllast">
                      {zahl((r.spitzeTrockenW ?? 0) / 1000, 1)}
                    </span>
                    <span style={{ fontWeight: 400, textAlign: 'right',
                                   minWidth: 28 }}
                          className={r.anlagenKaelteleistungW === null
                            ? 'sehr-schwach' : undefined}
                          title={r.anlagenKaelteleistungW === null
                            ? 'Keine Kühl-Zulufttemperatur hinterlegt — '
                              + 'keine Anlagenrechnung'
                            : 'Anlagenkälteleistung zur eigenen '
                              + `Spitzenstunde ${r.anlagenKaelteleistungStunde}:00`}>
                      {r.anlagenKaelteleistungW === null
                        ? ''
                        : zahl(r.anlagenKaelteleistungW / 1000, 1)}
                    </span>
                  </span>
                </button>
              ))}
            </div>
          ))}
        </div>
      </div>

      {raum
        ? <Raumblatt key={raum.id} raum={raum} detail={raumdetail} laedt={laedt} />
        : <div className="karte">
            <div className="leer">
              Links ein Geschoss aufklappen und einen Raum wählen.
            </div>
          </div>}
    </div>
  );
}

function Raumblatt({ raum, detail, laedt }: {
  raum: KuehllastErgebnisRaum;
  detail: KuehllastRaumDetail | null;
  laedt: boolean;
}) {
  const posten = [
    ['Solarer Eintrag', raum.strahlungW],
    ['Opake Bauteile', raum.opakW],
    ['Transmission Glas', raum.transparentW],
    ['Personen trocken', raum.personenTrockenW],
    ['Beleuchtung', raum.beleuchtungW],
    ['Geräte', raum.geraeteW],
    ['Lüftung trocken', raum.lueftungTrockenW],
  ] as const;

  const feucht = [
    ['Personen feucht', raum.personenFeuchtW],
    ['Lüftung feucht', raum.lueftungFeuchtW],
  ] as const;

  return (
    <div style={{ display: 'grid', gap: 18 }}>
      <div className="karte" style={{ position: 'sticky', top: 16, zIndex: 2 }}>
        <div className="bereich-kopf">
          <div>
            <h2 className="karte-titel" style={{ margin: 0 }}>
              {raum.raumnummer ?? '—'}{' '}
              <span className="schwach" style={{ fontWeight: 400 }}>
                {raum.raumname}
              </span>
            </h2>
            <p className="sehr-schwach" style={{ margin: '2px 0 0' }}>
              {[raum.ebene, raum.nutzung].filter(Boolean).join(' · ') || '—'}
            </p>
          </div>
          {raum.personenQuelle === 'profil' && (
            <span className="marke warnung">Personenzahl abgeleitet</span>
          )}
        </div>

        <div className="kennzahlen" style={{ marginTop: 16 }}>
          <div>
            <div className="kennzahl-wert">
              {zahl((raum.spitzeTrockenW ?? 0) / 1000, 2)}
            </div>
            <div className="kennzahl-name">kW trocken</div>
          </div>
          <div>
            <div className="kennzahl-wert">
              {zahl((raum.spitzeFeuchtW ?? 0) / 1000, 2)}
            </div>
            <div className="kennzahl-name">kW feucht</div>
          </div>
          <div>
            <div className="kennzahl-wert">{raum.spitzeStunde ?? '—'}:00</div>
            <div className="kennzahl-name">Spitzenstunde</div>
          </div>
          <div>
            <div className="kennzahl-wert">{zahl(raum.spezifischWm2, 0)}</div>
            <div className="kennzahl-name">W/m²</div>
          </div>
          <div>
            <div className="kennzahl-wert">{zahl(raum.spezifischWm3, 2)}</div>
            <div className="kennzahl-name">W/m³</div>
          </div>
          <div>
            <div className="kennzahl-wert">{zahl(raum.personen, 0)}</div>
            <div className="kennzahl-name">Personen</div>
          </div>
          <div>
            <div className="kennzahl-wert">{zahl(raum.solltemperaturC, 1)}</div>
            <div className="kennzahl-name">°C Sollwert</div>
          </div>
        </div>
      </div>

      {raum.warnungen && (
        <div className="karte">
          <div className="hinweis warnung">
            <span className="hinweis-zeichen">!</span>
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {raum.warnungen.split('; ').map((w, i) => <li key={i}>{w}</li>)}
            </ul>
          </div>
        </div>
      )}

      <div className="karte">
        <h3 className="karte-titel">Tagesgang</h3>
        {laedt && <div className="leer">lädt …</div>}
        {detail && detail.raum.id === raum.id && (
          <Tagesgang stunden={detail.stunden} />
        )}
      </div>

      <div className="karte">
        <h3 className="karte-titel">
          Posten zur Spitzenstunde
          <span className="sehr-schwach" style={{ fontWeight: 400,
                                                  marginLeft: 8 }}>
            {raum.spitzeStunde ?? '—'}:00
          </span>
        </h3>

        <table className="liste" style={{ marginTop: 8 }}>
          <tbody>
            {posten.map(([bez, w]) => (
              <tr key={bez}>
                <td>{bez}</td>
                <td style={{ textAlign: 'right', width: 120 }}>
                  {w === null || w === undefined ? '—' : `${zahl(w, 0)} W`}
                </td>
              </tr>
            ))}
            <tr>
              <td><strong>Trockene Kühllast</strong></td>
              <td style={{ textAlign: 'right' }}>
                <strong>{zahl(raum.spitzeTrockenW, 0)} W</strong>
              </td>
            </tr>
            {feucht.map(([bez, w]) => (
              <tr key={bez} className="sehr-schwach">
                <td>{bez}</td>
                <td style={{ textAlign: 'right' }}>
                  {w === null || w === undefined ? '—' : `${zahl(w, 0)} W`}
                </td>
              </tr>
            ))}
            <tr>
              <td><strong>Feuchte Last</strong></td>
              <td style={{ textAlign: 'right' }}>
                <strong>{zahl(raum.spitzeFeuchtW, 0)} W</strong>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="karte">
        <h3 className="karte-titel" style={{
          display: 'inline-flex', alignItems: 'center', gap: 7 }}>
          <IconLueftung size={16} /> Randbedingungen
        </h3>
        <table className="liste" style={{ marginTop: 8 }}>
          <tbody>
            <tr><td>Fläche</td>
                <td style={{ textAlign: 'right' }}>
                  {zahl(raum.flaecheM2, 1)} m²</td></tr>
            <tr><td>Außenluft</td>
                <td style={{ textAlign: 'right' }}>
                  {zahl(raum.aussenluftM3h, 0)} m³/h</td></tr>
            <tr><td>WRG-Grad</td>
                <td style={{ textAlign: 'right' }}>
                  {raum.wrgGrad === null
                    ? '—' : `${zahl(raum.wrgGrad * 100, 1)} %`}</td></tr>
            <tr><td>Anlagenkälteleistung</td>
                <td style={{ textAlign: 'right' }}>
                  {raum.anlagenKaelteleistungW === null
                    ? <span className="sehr-schwach"
                            title="Keine Kühl-Zulufttemperatur hinterlegt — keine Anlagenrechnung">
                        —
                      </span>
                    : <>
                        {zahl(raum.anlagenKaelteleistungW / 1000, 2)} kW
                        <span className="sehr-schwach" style={{ marginLeft: 6 }}
                              title="Eigene Spitzenstunde der Anlagenkälteleistung — nicht die Raumspitzenstunde">
                          {raum.anlagenKaelteleistungStunde}:00
                        </span>
                      </>}
                </td></tr>
            <tr className="sehr-schwach"><td>davon sensibel</td>
                <td style={{ textAlign: 'right' }}>
                  {raum.anlagenKaelteleistungSensibelW === null
                    ? '—' : `${zahl(raum.anlagenKaelteleistungSensibelW / 1000, 2)} kW`}
                </td></tr>
            <tr className="sehr-schwach"><td>davon latent</td>
                <td style={{ textAlign: 'right' }}>
                  {raum.anlagenKaelteleistungLatentW === null
                    ? '—' : `${zahl(raum.anlagenKaelteleistungLatentW / 1000, 2)} kW`}
                </td></tr>
            <tr><td>Verglasung</td>
                <td style={{ textAlign: 'right' }}>
                  {zahl(raum.fensterflaecheM2, 1)} m²
                  {raum.fensterOhneGWert > 0 && (
                    <span className="marke warnung" style={{ marginLeft: 6 }}>
                      {raum.fensterOhneGWert} ohne g-Wert
                    </span>
                  )}</td></tr>
            <tr><td>Bauteile</td>
                <td style={{ textAlign: 'right' }}>
                  {raum.anzahlBauteile}
                  {raum.bauteileOhneUWert > 0 && (
                    <span className="marke warnung" style={{ marginLeft: 6 }}>
                      {raum.bauteileOhneUWert} ohne U-Wert
                    </span>
                  )}</td></tr>
            <tr><td>Personenzahl</td>
                <td style={{ textAlign: 'right' }}>
                  {zahl(raum.personen, 0)}
                  <span className="sehr-schwach" style={{ marginLeft: 6 }}>
                    {raum.personenQuelle === 'profil' ? 'aus Nutzungsprofil'
                      : raum.personenQuelle === 'raumwert' ? 'aus Raumdaten'
                      : 'fehlt'}
                  </span></td></tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

// =============================================================================
//  Der Jahresgang
// =============================================================================

/**
 * TL3 (Fund LZ1): Die Vorwahl des Jahresgang-Reiters ist der MASSGEBENDE
 * Kühllastlauf (Auslegungslauf.KuehllastAsync über die API), nicht der
 * jüngste. Gibt es keinen, wird nichts vorgewählt - kein stiller Rückfall;
 * die Laufliste bleibt wählbar.
 */
export function jahresgangVorwahl(
  laeufe: KuehllastLaufKopf[], wahl: AuslegungslaufWahl | null,
): string | null {
  return wahl !== null && laeufe.some((l) => l.id === wahl.laufId) ? wahl.laufId : null;
}

export function JahresgangReiter({ api, projektId }: {
  api: ApiClient; projektId: string;
}) {
  const [laeufe, setLaeufe] = useState<KuehllastLaufKopf[]>([]);
  const [bestand, setBestand] = useState<Record<string, number>>({});
  const [messreiheVorhanden, setMessreiheVorhanden] = useState(true);
  const [gewaehlt, setGewaehlt] = useState<string | null>(null);
  const [jahr, setJahr] = useState<KuehllastJahresgang | null>(null);
  const [laedt, setLaedt] = useState(true);
  const [rechnet, setRechnet] = useState(false);
  const [sekunden, setSekunden] = useState(0);
  const [ergebnis, setErgebnis] =
    useState<KuehllastJahresgangErgebnis | null>(null);
  const [pdf, setPdf] = useState<string | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  // --- Die Läufe und ihr Jahresgangbestand ---------------------------------
  useEffect(() => {
    let weg = false;
    (async () => {
      try {
        const [l, b, a] = await Promise.all([
          api.kuehllastLaeufe(projektId),
          api.kuehllastJahresgangBestand(projektId),
          api.kuehllastAuslegungslauf(projektId),
        ]);
        if (weg) return;

        setLaeufe(l);
        setBestand(Object.fromEntries(
          b.bestand.map((x) => [x.laufId, x.stunden])));
        setMessreiheVorhanden(b.messreiheVorhanden);
        setGewaehlt(jahresgangVorwahl(l, a.wahl));
      } catch (err) {
        if (!weg) setFehler(err instanceof Error ? err.message : String(err));
      } finally {
        if (!weg) setLaedt(false);
      }
    })();
    return () => { weg = true; };
  }, [api, projektId]);

  // --- Der Jahresgang des gewählten Laufs ----------------------------------
  //
  // Eigener Effekt und nicht im Ladeeffekt oben: Die Laufwahl wechselt ihn,
  // ohne die Liste neu zu holen.
  useEffect(() => {
    if (gewaehlt === null) { setJahr(null); return; }

    let weg = false;
    (async () => {
      try {
        const j = await api.kuehllastJahresgang(gewaehlt);
        if (!weg) { setJahr(j); setMessreiheVorhanden(j.messreiheVorhanden); }
      } catch (err) {
        if (!weg) setFehler(err instanceof Error ? err.message : String(err));
      }
    })();
    return () => { weg = true; };
  }, [api, gewaehlt]);

  // --- Die verstrichene Zeit -----------------------------------------------
  //
  // Der Lauf meldet nichts, solange er läuft, und rechnet 8.760 Stunden über
  // rund 670 Räume — gemessen am 16.09.2026 gegen eine Kopie der
  // Produktionsdaten: 10,4 s für Lauf 15. Auf einer belasteten Maschine kann
  // daraus ein Vielfaches werden. Ein Knopf, der stumm bleibt, sieht aus wie
  // ein Hänger — deshalb die laufende Sekunde.
  useEffect(() => {
    if (!rechnet) return;

    const start = Date.now();
    setSekunden(0);
    const zeiger = window.setInterval(
      () => setSekunden(Math.round((Date.now() - start) / 1000)), 1000);

    return () => window.clearInterval(zeiger);
  }, [rechnet]);

  const stundenDesLaufs = gewaehlt === null ? 0 : (bestand[gewaehlt] ?? 0);

  const rechnen = async () => {
    if (gewaehlt === null || rechnet) return;

    setRechnet(true);
    setFehler(null);
    setErgebnis(null);
    try {
      const e = await api.kuehllastJahresgangRechnen(gewaehlt);
      setErgebnis(e);

      if (!e.erfolg) {
        setFehler(e.zusammenfassung.join(' '));
        return;
      }

      setBestand((b) => ({ ...b, [gewaehlt]: e.stunden }));
      setJahr(await api.kuehllastJahresgang(gewaehlt));
      setLaeufe(await api.kuehllastLaeufe(projektId));
    } catch (err) {
      setFehler(err instanceof Error ? err.message : String(err));
    } finally {
      setRechnet(false);
    }
  };

  /**
   * Einen der beiden Nachweise holen.
   *
   * Der Dateiname kommt aus der Antwort und traegt die Dokumentnummer vorn -
   * genau den Namen, der im Berichtsregister steht.
   */
  const holePdf = async (was: 'jahr' | 'last') => {
    if (gewaehlt === null || pdf !== null) return;
    setPdf(was);
    setFehler(null);
    try {
      if (was === 'jahr') await api.kuehllastJahresgangBericht(gewaehlt);
      else await api.kuehllastBericht(gewaehlt, 'raeume');
    } catch (err) {
      setFehler(err instanceof Error ? err.message : String(err));
    } finally {
      setPdf(null);
    }
  };

  if (laedt)
    return <div className="karte"><div className="leer">lädt …</div></div>;

  if (laeufe.length === 0)
    return (
      <div className="karte">
        <div className="leer">
          Noch kein Kühllastlauf. Im Reiter „Kühllast" einen rechnen.
        </div>
      </div>
    );

  const lauf = laeufe.find((l) => l.id === gewaehlt) ?? laeufe[0];
  const k = jahr?.kennzahlen ?? null;
  const hatJahresgang = (jahr?.tage.length ?? 0) > 0;

  // --- Die Steuerkarte -----------------------------------------------------
  //
  // Sie steht IMMER, auch im Leerzustand: Der Auslöser gehört genau dorthin,
  // wo der Befund sichtbar wird.
  const steuerung = (
    <div className="karte">
      <div className="bereich-kopf">
        <h2 className="karte-titel" style={{ margin: 0,
              display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <IconKuehlung size={18} /> Jahresgang des Gebäudes
        </h2>
        <span className={`marke ${stundenDesLaufs > 0 ? 'neutral' : 'warnung'}`}>
          {stundenDesLaufs > 0
            ? `${zahl(stundenDesLaufs, 0)} Stunden`
            : 'ohne Jahresgang'}
        </span>
      </div>

      <details className="erlaeuterung">
        <summary>Erläuterung</summary>
        <div>
          Derselbe Rechenweg wie am Auslegungstag, angewendet auf alle
          Stunden der DWD-Messreihe für München-Flughafen. Beantwortet die
          Fragen, die ein Auslegungstag nicht beantwortet: wie viele Stunden
          im Jahr überhaupt gekühlt wird, wie viel Arbeit anfällt, und in
          wie vielen dieser Stunden die Außenluft kühl genug für freie
          Kühlung wäre. Ein Lauf ohne Jahresgang lässt sich hier nachtragen —
          er behält dabei seine Nummer und seine Raumergebnisse. Weil die
          Raumkoeffizienten eines Laufs nicht gespeichert sind, rechnet der
          Nachtrag mit der heutigen Hülle und hält den Auslegungstag des
          Laufs dagegen.
        </div>
      </details>

      <div style={{ display: 'flex', gap: 10, alignItems: 'center',
                    flexWrap: 'wrap', marginTop: 12 }}>
        <label htmlFor="jg-lauf" className="sehr-schwach">Lauf</label>
        <select id="jg-lauf" value={gewaehlt ?? ''}
                disabled={rechnet}
                onChange={(e) => { setGewaehlt(e.target.value || null);
                                   setErgebnis(null); }}>
          {gewaehlt === null && (
            <option value="">kein maßgebender Lauf – bitte wählen</option>
          )}
          {laeufe.map((l) => (
            <option key={l.id} value={l.id}>
              Lauf {l.laufnummer}
              {' · '}{zahl((l.spitzeTrockenW ?? 0) / 1000, 0)} kW
              {(bestand[l.id] ?? 0) > 0
                ? ` · ${zahl(bestand[l.id], 0)} Jahresstunden`
                : ' · ohne Jahresgang'}
            </option>
          ))}
        </select>

        <button className="knopf betont" disabled={rechnet || !messreiheVorhanden}
                onClick={() => void rechnen()}>
          {rechnet
            ? `rechnet … ${zahl(sekunden, 0)} s`
            : stundenDesLaufs > 0
              ? 'Jahresgang neu rechnen'
              : 'Jahresgang rechnen'}
        </button>

        {rechnet && (
          <span className="sehr-schwach">
            8.760 Stunden über {zahl(lauf.anzahlRaeume, 0)} Räume. Das Fenster
            nicht schließen.
          </span>
        )}
      </div>

      {/* --- Die Nachweise -------------------------------------------------
          Für den OBEN GEWÄHLTEN Lauf und nicht für den jüngsten: Die
          Laufwahl ist die eine Entscheidung dieser Karte, und ein Knopf, der
          sie ignoriert, erzeugt einen Nachweis über einen anderen Stand als
          den, den der Betrachter gerade liest.

          Der Jahresgangknopf bleibt gesperrt, solange der gewählte Lauf
          keine Zeilen hat: Der Endpunkt antwortete dann mit 409 „Es gibt
          nichts auszugeben". */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center',
                    flexWrap: 'wrap', marginTop: 12, paddingTop: 10,
                    borderTop: '1px solid var(--linie)' }}>
        <span className="sehr-schwach" style={{ marginRight: 4 }}>
          Als PDF:
        </span>

        <button className="knopf"
                disabled={pdf !== null || rechnet || stundenDesLaufs === 0}
                onClick={() => void holePdf('jahr')}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <IconPdf size={14} />
          {pdf === 'jahr' ? 'erzeugt …' : 'Jahresgang'}
        </button>

        <button className="knopf" disabled={pdf !== null || rechnet}
                onClick={() => void holePdf('last')}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <IconPdf size={14} />
          {pdf === 'last' ? 'erzeugt …' : 'Kühllastbericht'}
        </button>

        <GewerkUnterlagenZip
          api={api} projektId={projektId} gewerk="lasten"
          beschriftung="Lasten-Unterlagen als ZIP" />
      </div>

      {fehler && (
        <div className="hinweis fehler" style={{ marginTop: 10 }}>
          <span className="hinweis-zeichen">!</span><div>{fehler}</div>
        </div>
      )}

      {ergebnis?.erfolg && (
        <div className="hinweis" style={{ marginTop: 10 }}>
          <span className="hinweis-zeichen">i</span>
          <div>
            Lauf {ergebnis.laufnummer}: {zahl(ergebnis.stunden, 0)} Stunden in{' '}
            {zahl(ergebnis.sekunden, 0)} s gerechnet,{' '}
            {zahl(ergebnis.kuehlstunden, 0)} davon mit Kühlbedarf.
            Auslegungstag des Laufs{' '}
            {zahl(ergebnis.auslegungSpitzeKw, 0)} kW, mit der heutigen Hülle
            nachgerechnet {zahl(ergebnis.gegenprobeSpitzeKw, 0)} kW
            ({ergebnis.abweichungProzent >= 0 ? '+' : ''}
            {zahl(ergebnis.abweichungProzent, 1)} %).
          </div>
        </div>
      )}

      {ergebnis && ergebnis.vorbehalte.length > 0 && (
        <div className="hinweis warnung" style={{ marginTop: 10 }}>
          <span className="hinweis-zeichen">!</span>
          <div>
            <strong>Vorbehalt dieses Nachtrags</strong>
            <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
              {ergebnis.vorbehalte.map((v, i) => <li key={i}>{v}</li>)}
            </ul>
          </div>
        </div>
      )}
    </div>
  );

  // --- Der Leerzustand, richtig begründet ----------------------------------
  //
  // BEFUND 16.09.2026 (Fachplaner): Hier stand für JEDEN Lauf ohne
  // Jahresgangzeilen „es liegt keine Messreihe vor". Bei den Läufen 13 bis 15
  // war das falsch — die Messreihe hat 8.760 Stunden, es fehlte nur der
  // Auslöser. Eine falsche Diagnose ist schlimmer als keine: Sie schickt den
  // Leser zu den Klimadaten statt zum Knopf daneben.
  if (gewaehlt === null)
    return (
      <>
        {steuerung}
        <div className="karte" data-test="jahresgang-ohne-vorwahl">
          <div className="leer">
            Kein maßgebender Kühllastlauf bestimmbar: Keiner ist als maßgebend
            gekennzeichnet, und es gibt keinen fertigen Lauf, der kein
            Vergleichslauf ist. Oben einen Lauf wählen.
          </div>
        </div>
      </>
    );

  if (!hatJahresgang)
    return (
      <>
        {steuerung}
        <div className="karte" style={{ marginTop: 18 }}>
          <div className="leer">
            {messreiheVorhanden
              ? `Lauf ${lauf.laufnummer} wurde ohne Jahresgang gerechnet. `
                + 'Die Messreihe liegt vor — der Jahresgang lässt sich oben '
                + 'nachtragen, ohne den Lauf zu ersetzen.'
              : 'Für dieses Projekt liegt keine Klimamessreihe vor '
                + '(heizlast_klimadatensatz mit Verwendung „messreihe"). '
                + 'Ohne sie lässt sich kein Jahresgang rechnen — erst die '
                + 'Messreihe einspielen.'}
          </div>
        </div>
      </>
    );

  return (
    <>
      {steuerung}

      {k && (
        <div className="karte" style={{ marginTop: 18 }}>
          <h3 className="karte-titel">Kennzahlen des Jahres</h3>

          <div className="kennzahlen" style={{ marginTop: 16 }}>
            <div>
              <div className="kennzahl-wert">{zahl(k.spitzeW / 1000, 0)}</div>
              <div className="kennzahl-name">kW Jahresspitze</div>
            </div>
            <div>
              <div className="kennzahl-wert">
                {k.spitzeTag}.{k.spitzeMonat}.
              </div>
              <div className="kennzahl-name">
                Tag der Spitze, {k.spitzeStunde}:00
              </div>
            </div>
            <div>
              <div className="kennzahl-wert">{zahl(k.arbeitMwh, 0)}</div>
              <div className="kennzahl-name">MWh Kühlarbeit</div>
            </div>
            <div>
              <div className="kennzahl-wert">{zahl(k.kuehlstunden, 0)}</div>
              <div className="kennzahl-name">
                Kühlstunden von {zahl(k.stunden, 0)}
              </div>
            </div>
            <div>
              <div className="kennzahl-wert">
                {zahl(k.freieKuehlungStunden, 0)}
              </div>
              <div className="kennzahl-name">
                davon unter 22 °C außen
              </div>
            </div>
            <div>
              <div className="kennzahl-wert">{zahl(k.spitzeAussenC, 1)}</div>
              <div className="kennzahl-name">°C zur Jahresspitze</div>
            </div>
          </div>

          {k.freieKuehlungStunden > 0 && (
            <p className="sehr-schwach" style={{ marginTop: 10 }}>
              In {zahl(k.freieKuehlungStunden, 0)} der {zahl(k.kuehlstunden, 0)}{' '}
              Kühlstunden liegt die Außentemperatur unter 22 °C — dort ließe
              sich ohne Kältemaschine kühlen.
            </p>
          )}
        </div>
      )}

      <div className="karte" style={{ marginTop: 18 }}>
        <h3 className="karte-titel">Tagesmaxima über das Jahr</h3>
        <Jahreskurve tage={jahr!.tage} />
      </div>

      <div className="karte" style={{ marginTop: 18 }}>
        <h3 className="karte-titel">Monatsbilanz</h3>
        <table className="liste" style={{ marginTop: 8 }}>
          <thead>
            <tr>
              <th>Monat</th>
              <th style={{ textAlign: 'right' }}>Spitze</th>
              <th style={{ textAlign: 'right' }}>Kühlarbeit</th>
              <th style={{ textAlign: 'right' }}>Kühlstunden</th>
            </tr>
          </thead>
          <tbody>
            {jahr!.monate.map((m) => (
              <tr key={m.monat}>
                <td>{MONATE[m.monat - 1]}</td>
                <td style={{ textAlign: 'right' }}>
                  {zahl(m.maxTrockenW / 1000, 0)} kW
                </td>
                <td style={{ textAlign: 'right' }}>
                  {zahl(m.arbeitKwh / 1000, 1)} MWh
                </td>
                <td style={{ textAlign: 'right' }} className="schwach">
                  {zahl(m.kuehlstunden, 0)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}


/** 365 Tagesmaxima als Säulen, mit der Außentemperatur als Linie. */
function Jahreskurve({ tage }: {
  tage: KuehllastJahresgang['tage'];
}) {
  const breite = 900, hoehe = 220;
  const links = 56, rechts = 44, oben = 12, unten = 26;
  const zB = breite - links - rechts, zH = hoehe - oben - unten;

  const max = Math.max(1, ...tage.map((t) => t.maxTrockenW));
  const min = Math.min(0, ...tage.map((t) => t.maxTrockenW));

  const temps = tage.map((t) => t.maxAussenC)
    .filter((t): t is number => t !== null);
  const tMax = temps.length > 0 ? Math.max(...temps) : 1;
  const tMin = temps.length > 0 ? Math.min(...temps) : 0;

  const y = (w: number) => oben + zH * (1 - (w - min) / (max - min));
  const x = (i: number) => links + zB * (i / Math.max(1, tage.length - 1));
  const yT = (t: number) => oben + zH
    * (1 - (t - tMin) / (tMax - tMin === 0 ? 1 : tMax - tMin));

  const saeule = zB / Math.max(1, tage.length);

  // Monatsgrenzen für die Achse
  const grenzen: { i: number; monat: number }[] = [];
  let letzter = 0;
  tage.forEach((t, i) => {
    if (t.monat !== letzter) { grenzen.push({ i, monat: t.monat }); letzter = t.monat; }
  });

  return (
    <div style={{ overflowX: 'auto' }}>
      <svg viewBox={`0 0 ${breite} ${hoehe}`} width="100%"
           style={{ maxWidth: breite, display: 'block' }}
           role="img" aria-label="Jahresgang der Kühllast">
        {[0, 0.5, 1].map((f) => {
          const w = min + (max - min) * f;
          return (
            <g key={f}>
              <line x1={links} x2={breite - rechts} y1={y(w)} y2={y(w)}
                    stroke="var(--linie)" />
              <text x={links - 6} y={y(w) + 3} textAnchor="end" fontSize={10}
                    fill="var(--text-sehr-schwach)">
                {zahl(w / 1000, 0)} kW
              </text>
            </g>
          );
        })}

        {tage.map((t, i) => (
          <rect key={i} x={x(i) - saeule / 2}
                y={Math.min(y(t.maxTrockenW), y(0))}
                width={Math.max(0.6, saeule * 0.9)}
                height={Math.abs(y(t.maxTrockenW) - y(0))}
                fill={t.maxTrockenW >= 0 ? '#4f8fbf' : '#9ac09a'} />
        ))}

        <line x1={links} x2={breite - rechts} y1={y(0)} y2={y(0)}
              stroke="var(--anthrazit)" />

        {temps.length > 0 && (
          <path fill="none" stroke="var(--schlecht)" strokeWidth={1}
                strokeOpacity={0.7}
                d={tage.map((t, i) => t.maxAussenC === null ? null
                    : `${i === 0 ? 'M' : 'L'}${x(i)},${yT(t.maxAussenC)}`)
                  .filter(Boolean).join(' ')} />
        )}

        {grenzen.map((g) => (
          <text key={g.monat} x={x(g.i)} y={hoehe - 8} fontSize={10}
                textAnchor="start" fill="var(--text-sehr-schwach)">
            {MONATE[g.monat - 1]}
          </text>
        ))}
      </svg>

      <p className="sehr-schwach" style={{ fontSize: 12, marginTop: 6 }}>
        Blau: Tagesmaximum der trockenen Kühllast. Grün: Tage, an denen das
        Gebäude auch im Maximum keinen Kühlbedarf hat. Rot gestrichelt: höchste
        Außentemperatur des Tages.
      </p>
    </div>
  );
}
